import type { PartnerPortalBoundaryInput } from "./partnerPortalBoundary";

import { buildPartnerPortalPreview, isPartnerVisibleOpportunity } from "./partnerPortalBoundary";
import { isHouseAccountOpportunity } from "./opportunityPipelineStatus";
import type {
  PartnerPortalTaxSummary,
  RevenuePartner,
  RevenueOpportunity,
  RevenuePartnerUserLink,
  Tax1099ReadinessRow,
  PartnerPortalReadBundle,
  PartnerPortalSharedOpportunity,
} from "../../types/revenueOps";

export type PartnerPortalReadModelRecords = {
  links: RevenuePartnerUserLink[];
  partners: RevenuePartner[];
  opportunities?: PartnerPortalBoundaryInput["opportunities"];
  attributions?: PartnerPortalBoundaryInput["attributions"];
  commission_entries?: PartnerPortalBoundaryInput["commission_entries"];
  payouts?: PartnerPortalBoundaryInput["payouts"];
  payout_items?: PartnerPortalBoundaryInput["payout_items"];
  tax_rows?: Tax1099ReadinessRow[];
  relationships?: PartnerPortalBoundaryInput["relationships"];
};

export function getActivePartnerLinksForUser(userId: string, links: RevenuePartnerUserLink[], partners: RevenuePartner[]): RevenuePartnerUserLink[] {
  const activePartnerIds = new Set(partners.filter((partner) => partner.status === "active").map((partner) => partner.id));
  return links.filter((link) => link.user_id === userId && link.is_active && activePartnerIds.has(link.partner_id));
}

export function canUserAccessPartner(userId: string, partnerId: string, links: RevenuePartnerUserLink[], partners: RevenuePartner[]): boolean {
  return getActivePartnerLinksForUser(userId, links, partners).some((link) => link.partner_id === partnerId);
}

function emptyBundle(userId: string, linkedPartnerIds: string[], selectedPartnerId: string | undefined, warning: string): PartnerPortalReadBundle {
  return { current_user_id: userId, linked_partner_ids: linkedPartnerIds, selected_partner_id: selectedPartnerId, opportunities: [], shared_opportunities: [], commissions: [], direct_commissions: [], advisory_override_commissions: [], direct_commission_total: 0, advisory_override_total: 0, combined_commission_total: 0, payouts: [], direct_payout_total: 0, advisory_override_payout_total: 0, access_warnings: [warning] };
}

function sharedOpportunityStatus(item: RevenueOpportunity, selectedPartnerId: string): PartnerPortalSharedOpportunity["claim_status"] {
  if (["won", "closed_won"].includes(item.stage)) return "won";
  if (isHouseAccountOpportunity(item)) return "house_account";
  if (item.request_type === "claim_request" && item.request_status === "pending_review") return "ownership_request_pending";
  if (item.ownership_conflict || item.attribution_review === "pending_review" || item.attribution_review === "shared_exception") return "in_review";
  if (item.partner_id && item.ownership_window_status === "expired") return "expired";
  if (item.partner_id && item.partner_id !== selectedPartnerId) return "protected";
  if (item.partner_id) return "claimed";
  return "open";
}

function deriveSharedOpportunity(item: RevenueOpportunity, selectedPartnerId: string): PartnerPortalSharedOpportunity {
  const won = ["won", "closed_won"].includes(item.stage);
  return {
    id: item.id,
    account_label: item.account_name,
    website: item.website,
    parent_entity_label: item.parent_entity_label || null,
    category: item.opportunity_type,
    stage: item.stage,
    claim_status: item.partner_id === selectedPartnerId ? "yours" : sharedOpportunityStatus(item, selectedPartnerId),
    created_at: item.created_at,
    updated_at: item.updated_at,
    partner_visible_notes: item.partner_visible_note,
    ownership_window_status: won ? "not_set" : item.ownership_window_status || "not_set",
  };
}

export function isArchivedOpportunity(item: RevenueOpportunity): boolean {
  const row = item as RevenueOpportunity & { archivedAt?: string; deleted_at?: string; status?: string; metadata?: Record<string, unknown> };
  const metadata = row.metadata || {};
  return Boolean(
    row.archived_at || row.archivedAt || row.deleted_at || row.status === "archived" || row.stage === ("archived" as RevenueOpportunity["stage"]) ||
    metadata.archived_at || metadata.archivedAt || (metadata.archive_reason && (metadata.archived_at || metadata.archivedAt)),
  );
}

function buildTaxSummary(row: Tax1099ReadinessRow | undefined): PartnerPortalTaxSummary | undefined {
  if (!row) return undefined;
  return {
    tax_year: row.tax_year,
    readiness_status: row.readiness_status,
    paid_payout_total: row.paid_payout_total,
    prepared_payout_total: row.prepared_payout_total,
    reportable_candidate: row.reportable_candidate,
    blockers: row.blockers || [],
  };
}

export function derivePartnerPortalReadBundle(userId: string, partnerId: string | undefined, records: PartnerPortalReadModelRecords): PartnerPortalReadBundle {
  const links = getActivePartnerLinksForUser(userId, records.links, records.partners);
  const linkedPartnerIds = [...new Set(links.map((link) => link.partner_id))];
  if (!links.length) return emptyBundle(userId, [], partnerId, "No active sales_partner_users link grants partner-safe data.");
  if (!partnerId) return emptyBundle(userId, linkedPartnerIds, undefined, "Select one linked partner to preview partner-safe data.");
  if (!canUserAccessPartner(userId, partnerId, records.links, records.partners)) return emptyBundle(userId, linkedPartnerIds, partnerId, "This user is not actively linked to the selected active partner.");

  const partner = records.partners.find((item) => item.id === partnerId);
  if (!partner) return emptyBundle(userId, linkedPartnerIds, partnerId, "The selected partner could not be resolved.");
  const activeOpportunities = (records.opportunities || []).filter((item) => !isArchivedOpportunity(item));
  const preview = buildPartnerPortalPreview({
    partner,
    opportunities: activeOpportunities,
    attributions: records.attributions,
    commission_entries: records.commission_entries,
    payouts: records.payouts,
    payout_items: records.payout_items,
    tax_rows: records.tax_rows,
    relationships: records.relationships,
  });
  const allOpportunities = activeOpportunities;
  const shared_opportunities = allOpportunities
    .filter(isPartnerVisibleOpportunity)
    .map((item) => deriveSharedOpportunity(item, partnerId));
  const direct_commissions = preview.commissions.filter((item) => item.commission_category !== "advisory_override");
  const advisory_override_commissions = preview.commissions.filter((item) => item.commission_category === "advisory_override");
  const direct_payout_total = preview.payouts.filter((item) => item.payout_category !== "advisory_override").reduce((sum, item) => sum + item.amount, 0);
  const advisory_override_payout_total = preview.payouts.filter((item) => item.payout_category === "advisory_override").reduce((sum, item) => sum + item.amount, 0);
  return {
    current_user_id: userId,
    linked_partner_ids: linkedPartnerIds,
    selected_partner_id: partnerId,
    summary: preview.summary,
    opportunities: preview.opportunities,
    shared_opportunities,
    commissions: preview.commissions,
    direct_commissions,
    advisory_override_commissions,
    direct_commission_total: direct_commissions.reduce((sum, item) => sum + item.commission_amount, 0),
    advisory_override_total: advisory_override_commissions.reduce((sum, item) => sum + item.commission_amount, 0),
    combined_commission_total: preview.commissions.reduce((sum, item) => sum + item.commission_amount, 0),
    payouts: preview.payouts,
    direct_payout_total,
    advisory_override_payout_total,
    tax_summary: buildTaxSummary(records.tax_rows?.find((row) => row.partner_id === partnerId)),
    access_warnings: preview.access_allowed ? [] : [preview.access_reason || "Partner-safe data is unavailable."]
  };
}
